import type { PurchaseOrderDetail } from '../../../../services/purchaseOrders';
import { WizardModalShell } from '../../WizardModalShell';
import { WizardStepIndicator, type WizardStepDefinition } from '../../WizardStepIndicator';
import { useCreatePurchaseOrderWizard } from './useCreatePurchaseOrderWizard';
import { SelectPracticeStep } from './SelectPracticeStep';
import { SelectChargeStep } from './SelectChargeStep';
import { SelectLineItemsStep } from './SelectLineItemsStep';
import { SelectOrderDetailsStep } from './SelectOrderDetailsStep';
import { PurchaseOrderSummaryPane } from './PurchaseOrderSummaryPane';

interface CreatePurchaseOrderModalProps {
    /** Preselects the practice when opened from a practice-scoped context. */
    initialPracticeId?: string | null;
    onClose: () => void;
    onCreated: (created: PurchaseOrderDetail) => void;
}

// Three steps mirroring CreateChargeModal: Details (practice + charge),
// Line items, Order details (vendor, method, PO number, placed date).
const STEPS: readonly WizardStepDefinition[] = [
    { title: 'Details' },
    { title: 'Line items' },
    { title: 'Order details' }
];

// Records an order a human already placed on a vendor's site - never sends
// an email, and the summary pane's footnote says so explicitly.
export function CreatePurchaseOrderModal({
    initialPracticeId,
    onClose,
    onCreated
}: CreatePurchaseOrderModalProps) {
    const wizard = useCreatePurchaseOrderWizard({
        initialPracticeId,
        onCreated: (created) => {
            onCreated(created);
            onClose();
        }
    });
    const { draft, isSubmitting, stepIndex } = wizard;
    const isLastStep = stepIndex === STEPS.length - 1;

    return (
        <WizardModalShell
            title="New purchase order"
            onClose={onClose}
            dismissible={!isSubmitting}
            labelledBy="create-po-title"
            stepIndicator={
                <WizardStepIndicator
                    steps={STEPS}
                    step={stepIndex}
                    maxReachable={wizard.maxReachable}
                    onStepClick={wizard.goToStep}
                />
            }
            onSubmit={(e) => {
                void wizard.handleSubmit(e);
            }}
            summaryPane={<PurchaseOrderSummaryPane draft={draft} />}
            primaryActions={
                isLastStep ? (
                    <button
                        key="submit"
                        type="submit"
                        disabled={isSubmitting}
                        className="bg-black text-white rounded-xl px-6 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors shadow disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? 'Recording…' : 'Record order'}
                    </button>
                ) : (
                    <button
                        key="continue"
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            wizard.goNext();
                        }}
                        className="bg-black text-white rounded-xl px-6 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors shadow"
                    >
                        Continue
                    </button>
                )
            }
        >
            {wizard.step === 'details' && (
                <div className="flex flex-col gap-5">
                    <SelectPracticeStep
                        practiceId={draft.practiceId}
                        onChange={wizard.setPractice}
                    />
                    {draft.practiceId && (
                        <SelectChargeStep
                            practiceId={draft.practiceId}
                            chargeId={draft.chargeId}
                            onChange={(charge) => wizard.setCharge(charge.id)}
                        />
                    )}
                </div>
            )}
            {wizard.step === 'lines' && draft.chargeId && (
                <SelectLineItemsStep
                    chargeId={draft.chargeId}
                    selectedIds={draft.chargeLineItemIds}
                    onChange={(ids) => wizard.update({ chargeLineItemIds: ids })}
                />
            )}
            {wizard.step === 'order' && (
                <SelectOrderDetailsStep
                    vendorId={draft.vendorId}
                    orderMethodId={draft.orderMethodId}
                    poNumber={draft.poNumber}
                    sentAt={draft.sentAt}
                    onVendorChange={(vendorId) => wizard.update({ vendorId })}
                    onOrderMethodChange={(orderMethodId) => wizard.update({ orderMethodId })}
                    onPoNumberChange={(poNumber) => wizard.update({ poNumber })}
                    onSentAtChange={(sentAt) => wizard.update({ sentAt })}
                />
            )}
        </WizardModalShell>
    );
}
